// The game's authority on the server (see @tta/engine/Server/Authority): commits moves and hands out views over HTTP,
// and tells us when the game changes (server-sent events). The engine's turn controller uses it like the in-process
// one, except that committing returns a promise.
// Where the game server is: REACT_APP_API_URL if set; else, built for production, the address the app was loaded from
// (the server serves the app itself, see api/app.js); in development, the API's own dev server
import { IdentityHeaders, IdentityQuery } from './Identity';

export const ApiBase =process.env.REACT_APP_API_URL ?? (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:9000');

// (who we are goes with every request: see Identity.js)
const headers = (extra = {}) => ({ ...extra, ...IdentityHeaders() });

const json = async (response) => {
    const body = await response.json().catch(() => ({ ok: false, error: `Server answered ${response.status}` }));
    return response.ok || body.ok !== undefined ? body : { ok: false, error: body.error ?? `Server answered ${response.status}` };
};

// (and the sign-in cookie, if we signed in with a password, even to the development API at another address)
const credentials = 'include';

// A GET from the server, as JSON
export const Get = (path) => fetch(`${ApiBase}${path}`, { headers: headers(), credentials }).then(json);

// A POST of body to the server, as JSON
export const Post = (path, body) => fetch(`${ApiBase}${path}`, {
    method: 'POST',
    headers: headers({ 'Content-Type': 'application/json' }),
    credentials,
    body: JSON.stringify(body ?? {}),
}).then(json);

// Any other request (PUT, DELETE), with an optional JSON body
export const Send = (method, path, body) => fetch(`${ApiBase}${path}`, {
    method,
    headers: headers({ 'Content-Type': 'application/json' }),
    credentials,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
}).then(json);

export class RemoteAuthority {
    constructor(gameId)
    {
        this.gameId = gameId;
    }

    // { ok, error?, view }: the view is the committing player's, after their moves
    // (the player only counts with the development tools: otherwise the server plays our own seat)
    Commit(player, moves)
    {
        return Post(`/games/${this.gameId}/moves`, { player, moves });
    }

    // (likewise, player is only heard with the development tools)
    FetchView(player)
    {
        return Get(`/games/${this.gameId}/view?player=${player}`);
    }

    // The game's summary: { id, players, version, currentPlayer, actingPlayer, mySeat }
    FetchSummary()
    {
        return Get(`/games/${this.gameId}`);
    }

    // A finished game as it is kept, to look back over: { initial, commits } (see the engine's Review.js)
    FetchHistory()
    {
        return Get(`/games/${this.gameId}/history`);
    }

    // The game's chat: { messages: [{ id, seat, text, at }], seen } as the player sees it (player: as FetchView); a message
    // from them -> { ok, error?, message }; and that they have read it up to a message
    FetchChat(player)
    {
        return Get(`/games/${this.gameId}/chat?player=${player}`);
    }

    SendChat(player, text)
    {
        return Post(`/games/${this.gameId}/chat`, { player, text });
    }

    SeeChat(player, upTo)
    {
        return Post(`/games/${this.gameId}/chat/seen`, { player, upTo });
    }

    // Calls onUpdate(version) whenever the game changes, and onChat(message) for each new chat message (onChat() with
    // none: messages may have been missed, fetch the chat again); returns a function that stops listening.
    // Only while the tab is shown: each open stream holds one of the few connections a browser allows to a server (6),
    // so with several game tabs open, moves and views would wait in line behind them. A tab shown again listens again
    // and catches up with whatever changed meanwhile.
    Subscribe(onUpdate, onChat = () => {})
    {
        let source = null;
        const open = () => {
            if (source || document.hidden) return;
            source = new EventSource(`${ApiBase}/games/${this.gameId}/stream${IdentityQuery()}`, { withCredentials: true });
            source.addEventListener('update', (event) => onUpdate(JSON.parse(event.data).version));
            source.addEventListener('chat', (event) => onChat(JSON.parse(event.data)));
        };
        const close = () => {
            source?.close();
            source = null;
        };
        const visibility = () => {
            if (document.hidden)
            {
                close();
            } else {
                open();
                this.FetchSummary().then(summary => summary.version !== undefined && onUpdate(summary.version)).catch(() => {});
                onChat();
            }
        };
        open();
        document.addEventListener('visibilitychange', visibility);
        return () => {
            document.removeEventListener('visibilitychange', visibility);
            close();
        };
    }
}

// Starts a game on the server: players, their logins (ours among them; none: the development players); setup, one of
// the server's setups (none: its default); randomOrder: seated at random, else in the order given; title (none: its
// number). Returns its id, or throws the server's reason.
export const CreateGame = async (players, setup, randomOrder = false, title) => {
    const result = await Post('/games', { ...(players ? { players } : {}), ...(setup ? { setup } : {}), randomOrder, ...(title ? { title } : {}) });
    if (!result.id)
    {
        throw new Error(result.error ?? 'The game could not be started');
    }
    return result.id;
};
